import type { Enums } from "../types";

/**
 * The order an invoice's approvals are decided in. Every handover takes the next chain position,
 * and the newest one decides first: a default approver hands an invoice to A, A hands it to B, and
 * the chain runs B → A → default. A handover made later by the default approver goes to the front
 * all the same — C → B → A → default. The default approvers close the chain as a pool, any one of
 * whom decides that last step. Each step sees what the one before it accepted.
 *
 * A sole approval stands outside the chain: while one exists, only the sole approvals decide, and
 * each one may at any time.
 *
 * Written once for the server, which refuses a decision out of turn, and for the clients, which
 * show whose turn it is.
 */

/** What the chain reads of one approval. */
export interface ChainApproval {
	id: string;
	userId: string;
	assignment: Enums<"incomingInvoiceApprovalAssignment">;
	isSoleApprover: boolean;
	chainPosition: number;
	state: Enums<"incomingInvoiceApprovalState">;
}

/** Where an approval stands: its turn, waiting on the steps before it, decided, or out of it. */
export type ApprovalTurn = "now" | "waiting" | "done" | "closed";

/**
 * The approvals that decide the invoice, in the order they decide it: the sole ones alone while
 * there are any, else the handed-over ones newest first, then the default approvers.
 *
 * @param approvals - The invoice's approvals.
 */
export function getApprovalChain<Approval extends ChainApproval>(
	approvals: Approval[],
): Approval[] {
	const sole = approvals.filter((approval) => approval.isSoleApprover);
	if (sole.length) return sole;
	const assigned = approvals
		.filter((approval) => approval.assignment === "assigned")
		.sort((a, b) => b.chainPosition - a.chainPosition);
	const defaults = approvals.filter((approval) => approval.assignment === "default");
	return [...assigned, ...defaults];
}

/**
 * Whose turn it is on the invoice: the first handed-over approval still pending, else the default
 * approvers while none of them has decided. Null once a rejection ended the chain or every step
 * is decided.
 *
 * @param approvals - The invoice's approvals.
 * @returns `assigned` with the approval whose turn it is, `default` for the pool, or null.
 */
export function getChainTurn<Approval extends ChainApproval>(
	approvals: Approval[],
): { kind: "assigned"; approval: Approval } | { kind: "default" } | { kind: "sole" } | null {
	const chain = getApprovalChain(approvals);
	if (chain.some((approval) => approval.state === "rejected")) return null;
	if (chain.some((approval) => approval.isSoleApprover)) {
		return chain.some((approval) => approval.state === "pending") ? { kind: "sole" } : null;
	}
	for (const approval of chain) {
		if (approval.assignment === "assigned" && approval.state === "pending") {
			return { kind: "assigned", approval };
		}
	}
	const defaults = chain.filter((approval) => approval.assignment === "default");
	if (!defaults.length || defaults.some((approval) => approval.state !== "pending")) return null;
	return { kind: "default" };
}

/**
 * Where one approval stands in its invoice's chain.
 *
 * @param approval - The approval asked about.
 * @param approvals - All of the invoice's approvals.
 */
export function getApprovalTurn(approval: ChainApproval, approvals: ChainApproval[]): ApprovalTurn {
	const chain = getApprovalChain(approvals);
	if (!chain.some((step) => step.id === approval.id)) return "closed";
	if (approval.state !== "pending") return "done";
	const turn = getChainTurn(approvals);
	if (!turn) return "closed";
	if (turn.kind === "sole") return "now";
	if (turn.kind === "assigned") return turn.approval.id === approval.id ? "now" : "waiting";
	return approval.assignment === "default" ? "now" : "waiting";
}

/**
 * Whether a decided approval may still be taken back: only while nobody after it in the chain has
 * decided, since they decided on what it accepted.
 *
 * @param approval - The decided approval.
 * @param approvals - All of the invoice's approvals.
 */
export function canTakeBackApproval(approval: ChainApproval, approvals: ChainApproval[]) {
	if (approval.state === "pending" || approval.state === "skipped") return false;
	const chain = getApprovalChain(approvals);
	const index = chain.findIndex((step) => step.id === approval.id);
	if (index === -1) return false;
	for (const later of chain.slice(index + 1)) {
		if (later.state === "approved" || later.state === "rejected") return false;
	}
	return true;
}

/**
 * Whether the invoice carries a sole approval — the member handed it as sole decides it alone, and
 * nobody hands it over until that handover is taken back.
 *
 * @param approvals - The invoice's approvals.
 */
export function hasSoleApproval(approvals: Pick<ChainApproval, "isSoleApprover">[]) {
	return approvals.some((approval) => approval.isSoleApprover);
}

/**
 * Whether a member may hand the invoice over: a default approver of the invoice may, and so may
 * the member it was handed to last — the front of the chain — though only a default approver hands
 * it over as its sole approval. Nobody may while the invoice carries a sole approval.
 *
 * The server refuses the handover by this rule, and the web offers it by the same one.
 *
 * @param approvals - The invoice's approvals.
 * @param userId - The member asking.
 * @param isSole - Whether the handover would be the invoice's sole approval.
 */
export function canHandOver(approvals: ChainApproval[], userId: string, isSole: boolean) {
	if (hasSoleApproval(approvals)) return false;
	let front: ChainApproval | null = null;
	for (const approval of approvals) {
		if (approval.userId === userId && approval.assignment === "default") return true;
		if (approval.assignment !== "assigned") continue;
		if (!front || approval.chainPosition > front.chainPosition) front = approval;
	}
	return !isSole && front?.userId === userId;
}

/**
 * The approvals a default approver skips by deciding ahead of the chain: the handed-over ones
 * still pending.
 *
 * @param approvals - All of the invoice's approvals.
 */
export function getSkippedByDefault<Approval extends ChainApproval>(approvals: Approval[]) {
	return getApprovalChain(approvals).filter(
		(approval) => approval.assignment === "assigned" && approval.state === "pending",
	);
}

/**
 * The users whose turn it is to decide the invoice now: the sole approvers still pending while
 * there are any, the one handed-over approval at the front of the chain, or every default
 * approver still pending once the chain reaches the pool. Empty once the chain is closed.
 *
 * This is who a notification about a pending review goes to: a write that changes the set tells
 * the users newly in it.
 *
 * @param approvals - The invoice's approvals.
 * @returns The user ids on turn, in the order the approvals were given.
 */
export function getTurnUserIds(approvals: ChainApproval[]): string[] {
	const turn = getChainTurn(approvals);
	if (!turn) return [];
	if (turn.kind === "assigned") return [turn.approval.userId];
	const isOnTurn = (approval: ChainApproval) =>
		approval.state === "pending" &&
		(turn.kind === "sole" ? approval.isSoleApprover : approval.assignment === "default");
	return approvals.filter(isOnTurn).map((approval) => approval.userId);
}
